import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import AuthedNotFound from '../components/AuthedNotFound'
import Login from '../features/auth/pages/Login'
import Churches from '../features/churches/pages/Churches'
import Home from '../features/home/pages/Home'
import NotFound from '../pages/NotFound'
import App from '@/App'
import ErrorFallback from '@/components/ErrorFallback'
import Loader from '@/components/Loader'
import AuthedLayout from '@/features/auth/components/AuthedLayout'
import CompleteSetup from '@/features/auth/pages/CompleteSetup'
import ResetPassword from '@/features/auth/pages/ResetPassword'
import ForgotPasswordSuccess from '@/features/auth/pages/ForgotPasswordSuccess'
import RoleSelection from '@/features/auth/pages/RoleSelection'
import { useAuthStore } from '@/features/auth/store/authStore'
import Donations from '@/features/donations/pages/Donations'
import Registrations from '@/features/donations/pages/Registrations'
import Members from '@/features/members/pages/Members'
import UserProfile from '@/features/navbar/pages/UserProfile'
import { ServiceConfiguration } from '@/features/navbar/pages/ServiceConfiguration'
import { OAuth2Dashboard } from '@/features/oauth2'
import Reports from '@/features/reports/pages/Reports'
import Users from '@/features/users/pages/Users'
import Inventory from '@/features/inventory/pages/Inventory'
import Transactions from '@/features/inventory/pages/Transactions'
import Categories from '@/features/inventory/pages/Categories'
import { useInitializeData } from '@/hooks'
import { PermissionHelper } from '@/utils/permissions'
import AuditLogDashboard from '@/features/audit/pages/AuditLogDashboard'
import BackupDashboard from '@/features/backup/pages/BackupDashboard'
import AnnualSummary from '@/features/reports/pages/AnnualSummary'
import {
  auditLogFilterSchema,
  categoryFilterSchema,
  churchFilterSchema,
  donationFilterSchema,
  expensesFilterSchema,
  inventoryFilterSchema,
  memberFilterSchema,
  reportFilterSchema,
  transactionFilterSchema,
  userFilterSchema,
} from '@/schemas/filterSchemas'
import { UserRoleEnum } from '@/features/users/types/user'
import Expenses from '@/features/expenses/pages/Expenses'
import ExpensesCategories from '@/features/expenses/pages/ExpensesCategories'
import { PublicExpenseForm } from '@/features/expenses/pages/PublicExpenseForm'
import { PublicExpenseEditPage } from '@/features/expenses/pages/PublicExpenseEditPage'

// Helper function to check permissions before loading routes
function checkPermission(
  permissionCheck: (permissions: PermissionHelper) => boolean,
) {
  const { user, activeRole } = useAuthStore.getState()
  const permissions = new PermissionHelper({ user, activeRole })

  if (!permissionCheck(permissions)) {
    throw redirect({ to: '/' })
  }
}

const rootRoute = createRootRoute({
  component: () => <App />,
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => (
    <ErrorFallback
      error={error instanceof Error ? error : null}
      resetError={reset}
      showDetails={import.meta.env.DEV}
    />
  ),
})

// PUBLIC ROUTES (no authentication required)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
  beforeLoad: () => {
    // Redirect to home if already authenticated
    const { isAuthenticated, user } = useAuthStore.getState()
    if (isAuthenticated && user) {
      throw redirect({ to: '/' })
    }
  },
})

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$tempToken',
  component: ResetPassword,
})

export const completeSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/complete-setup/$token',
  component: CompleteSetup,
})

export const forgotPasswordSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password-requested',
  component: ForgotPasswordSuccess,
  validateSearch: (search) => ({
    email: (search as any)?.email || '',
  }),
})

// AUTHENTICATED ROUTES (require authentication)
export const roleSelectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/select-role',
  component: RoleSelection,
  beforeLoad: () => {
    const { isAuthenticated, user } = useAuthStore.getState()
    if (!isAuthenticated) throw redirect({ to: '/login' })

    // If user doesn't have multiple roles, redirect to home
    if (!user || user.roles.length <= 1) {
      throw redirect({ to: '/' })
    }
  },
})

function AuthedLayoutWithUsers() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const { isLoading, hasError } = useInitializeData()

  // CRITICAL: If not authenticated during render, force redirect
  // This handles the case where logout happens during data fetching
  useEffect(() => {
    if (!isAuthenticated || !user) {
      console.log(
        'Not authenticated in AuthedLayout, forcing redirect to login',
      )
      router.navigate({ to: '/login', replace: true })
    }
  }, [isAuthenticated, user])

  // If not authenticated, show nothing while redirect happens
  if (!isAuthenticated || !user) {
    return null
  }

  // Show loader while fetching data for authenticated users
  if (isLoading) {
    return <Loader />
  }

  // If there's an error, show error UI
  if (hasError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Failed to load data
          </h2>
          <p className="text-gray-600 mb-4">
            There was an error loading your data. Please try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    )
  }

  return <AuthedLayout />
}

export const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: () => {
    const { isAuthenticated, user, activeRole } = useAuthStore.getState()

    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      throw redirect({ to: '/login' })
    }

    // Check if token is expired
    if (user.expireAt) {
      const expiryDate = new Date(user.expireAt)
      const now = new Date()

      if (expiryDate.getTime() < now.getTime()) {
        console.log('Token expired in route guard, redirecting to login...')
        // Clear auth state
        useAuthStore.getState().logout()
        throw redirect({ to: '/login' })
      }
    }

    // Check if user needs to select a role
    if (user.roles.length > 1 && !activeRole) {
      throw redirect({ to: '/select-role' })
    }
  },
  component: AuthedLayoutWithUsers,
  notFoundComponent: AuthedNotFound,
})

export const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    const { user } = useAuthStore.getState()
    const roles = user?.roles ?? []
    if (roles.length === 1 && roles[0] === UserRoleEnum.INVENTORY) {
      throw redirect({ to: '/inventory' })
    }
    throw redirect({ to: '/home' })
  },
})

export const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'home',
  component: Home,
})

export const churchesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'churches', // Remove leading slash - it's relative to parent
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessChurches())
  },
  validateSearch: churchFilterSchema,
  component: Churches,
})

export const membersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'members', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessMembers())
  },
  validateSearch: memberFilterSchema,
  component: Members,
})

export const donationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'donations', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessDonations())
  },
  validateSearch: donationFilterSchema,
  component: Donations,
})

export const registrationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'registrations',
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessDonations())
  },
  validateSearch: donationFilterSchema,
  component: Registrations,
})

export const annualSummaryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'annual-summary',
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessAnnualSummary())
  },
  component: AnnualSummary,
})

export const reportsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'reports', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessReports())
  },
  validateSearch: reportFilterSchema,
  component: Reports,
})

export const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'settings', // Remove leading slash
  component: OAuth2Dashboard,
})

export const backupRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'backup', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessBackup())
  },
  component: BackupDashboard,
})

export const emailDashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'email-dashboard', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessEmailSettings())
  },
  component: OAuth2Dashboard,
})

export const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'profile', // Remove leading slash
  component: UserProfile,
})

export const configRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'donation-automation', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessDonationAutomation())
  },
  component: ServiceConfiguration,
})

export const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'users', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessUsers())
  },
  validateSearch: userFilterSchema,
  component: Users,
})

export const inventoryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'inventory',
  // Gate inventory access broadly for now; refine with specific permissions when available
  beforeLoad: () => {
    const { user } = useAuthStore.getState()
    const allowedRoles = [
      UserRoleEnum.SYSTEM_ADMIN.toString(),
      UserRoleEnum.DEVELOPER.toString(),
      UserRoleEnum.TREASURER.toString(),
      UserRoleEnum.PASTOR.toString(),
      UserRoleEnum.INVENTORY.toString(),
    ]
    const hasAccess = (user?.roles || []).some((r) => allowedRoles.includes(r))
    if (!hasAccess) throw redirect({ to: '/' })
  },
  validateSearch: inventoryFilterSchema,
  component: Inventory,
})

export const inventoryTransactionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'inventory/transactions',
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessInventory())
  },
  validateSearch: transactionFilterSchema,
  component: Transactions,
})

export const inventoryCategoriesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'inventory/categories',
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessInventory())
  },
  validateSearch: categoryFilterSchema,
  component: Categories,
})

export const expensesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'expenses',
  // Gate inventory access broadly for now; refine with specific permissions when available
  beforeLoad: () => {
    const { user } = useAuthStore.getState()
    const allowedRoles = [
      UserRoleEnum.SYSTEM_ADMIN.toString(),
      UserRoleEnum.DEVELOPER.toString(),
      UserRoleEnum.TREASURER.toString(),
      UserRoleEnum.PASTOR.toString(),
      UserRoleEnum.EXPENSES.toString(),
    ]
    const hasAccess = (user?.roles || []).some((r) => allowedRoles.includes(r))
    if (!hasAccess) throw redirect({ to: '/' })
  },
  validateSearch: expensesFilterSchema,
  component: Expenses,
})

export const expensesCategoriesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'expenses/categories',
  beforeLoad: () => {
    checkPermission((permissions) => permissions.isSystemAdmin())
  },
  validateSearch: categoryFilterSchema,
  component: ExpensesCategories,
})

export const auditLogDashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'audit', // Remove leading slash
  beforeLoad: () => {
    checkPermission((permissions) => permissions.canAccessAuditLog())
  },
  validateSearch: auditLogFilterSchema,
  component: AuditLogDashboard,
})

export const addExpenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/request-reimbursement',
  component: PublicExpenseForm,
})

export const fixExpenseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fix-expense/$token',
  component: PublicExpenseEditPage,
})

export const routeTree = rootRoute.addChildren([
  // Public routes (no auth required)
  loginRoute,
  resetPasswordRoute,
  completeSetupRoute,
  forgotPasswordSuccessRoute,
  addExpenseRoute,
  fixExpenseRoute,

  // Auth-required routes
  roleSelectionRoute,
  authedRoute.addChildren([
    indexRoute,
    homeRoute,
    churchesRoute,
    membersRoute,
    donationsRoute,
    registrationsRoute,
    annualSummaryRoute,
    settingsRoute,
    emailDashboardRoute,
    profileRoute,
    configRoute,
    usersRoute,
    reportsRoute,
    inventoryRoute,
    inventoryTransactionsRoute,
    inventoryCategoriesRoute,
    expensesRoute,
    expensesCategoriesRoute,
    auditLogDashboardRoute,
    backupRoute,
  ]),
])

// Custom search param stringifier that removes empty values
const stringifySearch = (search: Record<string, any>) => {
  const params = new URLSearchParams()

  Object.entries(search).forEach(([key, value]) => {
    // Skip falsy values except 0
    if (!value && value !== 0) return

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) return

    // Skip false booleans
    if (typeof value === 'boolean' && value === false) return

    // Skip objects with all null/undefined/empty values (like date ranges)
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const hasValidValue = Object.values(value).some(
        (v) => v !== null && v !== undefined && v !== '',
      )
      if (!hasValidValue) return
    }

    // Handle arrays
    if (Array.isArray(value)) {
      params.set(key, JSON.stringify(value))
    } else if (typeof value === 'object') {
      params.set(key, JSON.stringify(value))
    } else {
      params.set(key, String(value))
    }
  })

  const searchString = params.toString()
  return searchString ? `?${searchString}` : ''
}

// Custom search param parser
const parseSearch = (searchStr: string) => {
  const params = new URLSearchParams(searchStr)
  const result: Record<string, any> = {}

  params.forEach((value, key) => {
    try {
      // Try to parse as JSON (for arrays and objects)
      result[key] = JSON.parse(value)
    } catch {
      // If not JSON, use as string
      result[key] = value
    }
  })

  return result
}

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  stringifySearch,
  parseSearch,
})

// For dev tools
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
